import { ZOOM_3D_DEFAULT_FACTOR, ZOOM_3D_MAX_FACTOR, ZOOM_3D_MIN_FACTOR } from '@/config';
import type { Object3D } from 'three';
import type { ArcRotateCamera } from '@/infrastructure/engine3d/camera/ArcRotateCamera';
import type { CustomOrbitControls } from '@/infrastructure/engine3d/camera/controls/CustomOrbitControls';
import type { ICameraController } from '@/application/ports/ICameraController';

// Owns the user-facing camera operations behind ICameraController:
// zoomToLevel tweens through the underlying zoomOnSelection, autoRotate flips
// a controls flag. Stepping and clamping are the callers' business, through the
// shared zoom3dFactor policy — the lerp-based zoomIn / zoomOut nudges that used
// to live here widened the orbit limits on every call, so the bounds eroded
// permanently, and their 0.85 / 1.15 steps were not inverses.
//
// Extracted from ThreeEngine3D — the engine kept 6 `as
// CustomOrbitControls` casts to reach the same math from methods that
// weren't really its concern. Consolidating them here narrows the
// cast surface to this class alone.
class CameraController implements ICameraController {
    // True while our own framing tween is running. The tween moves the camera
    // through the controls, which report the move like any other, so without
    // this a listener would mistake our own animation for a user gesture and
    // resync the caller's intent to a half-finished position — two quick clicks
    // would then advance by less than one step each.
    private _isFraming = false;
    private readonly _camera: ArcRotateCamera;
    private readonly _controls: CustomOrbitControls;
    private readonly _getStrategyRoot: () => Object3D | undefined;

    constructor(params: { camera: ArcRotateCamera; getStrategyRoot: () => Object3D | undefined }) {
        this._camera = params.camera;
        this._controls = params.camera.controls as CustomOrbitControls;
        this._getStrategyRoot = params.getStrategyRoot;
    }

    setAutoRotate(enabled: boolean): void {
        this._controls.autoRotate = enabled;
    }

    // Read back rather than remembered: OrbitControls dollies on the wheel
    // without telling anyone, so a factor kept elsewhere desynced silently. It
    // then made the buttons step from a stale value — measured on a glass, ten
    // wheel notches left the camera at 1.800 while the stored factor still said
    // 1.1, and one click of zoom out threw the camera out to 4.315.
    getZoomFactor(): number {
        const fitRadius = this._getFitRadius();
        if (!fitRadius) {
            return ZOOM_3D_DEFAULT_FACTOR;
        }
        return this._camera.position.distanceTo(this._controls.target) / fitRadius;
    }

    // Puts the wheel on the same leash as the buttons. The controls' own limits
    // were unrelated to the framing, so the wheel could dolly past the closest
    // reachable button framing and out of the range the buttons report.
    syncZoomBounds(): void {
        const fitRadius = this._getFitRadius();
        if (!fitRadius) {
            return;
        }
        this._controls.minDistance = fitRadius * ZOOM_3D_MIN_FACTOR;
        this._controls.maxDistance = fitRadius * ZOOM_3D_MAX_FACTOR;
    }

    get isFraming(): boolean {
        return this._isFraming;
    }

    onCameraMoved(callback: () => void): () => void {
        this._controls.addEventListener('change', callback);
        return () => this._controls.removeEventListener('change', callback);
    }

    async zoomToLevel(level: number, durationMs = 750): Promise<void> {
        const target = this._getStrategyRoot();
        if (!target) {
            console.error('CameraController.zoomToLevel: no strategy root to frame');
            return;
        }
        // Refreshed here, not only once at mount: the framing distance is
        // computed from the camera, so orbiting shifts it — measured 3.502 to
        // 3.475 on a glass after one drag. Bounds fixed at mount would drift out
        // of step with the framing they are supposed to bracket.
        this.syncZoomBounds();
        this._isFraming = true;
        try {
            await this._camera.zoomOnSelection([target], level, durationMs);
        } finally {
            this._isFraming = false;
        }
    }

    private _getFitRadius(): number | undefined {
        const target = this._getStrategyRoot();
        if (!target) {
            return undefined;
        }
        return this._camera.getFitRadius([target]);
    }
}

export { CameraController };
